import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_RELOAD_STORAGE_KEY = "tvet-student-portal:chunk-reload";

const isChunkLoadError = (error: unknown): boolean => {
	if (!(error instanceof Error)) return false;

	const message = error.message.toLowerCase();
	return (
		message.includes("failed to fetch dynamically imported module") ||
		message.includes("importing a module script failed") ||
		message.includes("error loading dynamically imported module")
	);
};

/**
 * Lazy-load a route chunk and recover once after deploys when cached index.html points at stale assets.
 */
export function lazyWithRetry<T extends ComponentType<Record<string, never> | object>>(
	factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
	return lazy(() =>
		factory().catch((error: unknown) => {
			if (!isChunkLoadError(error)) {
				throw error;
			}

			const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
			if (!hasReloaded) {
				sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, "1");
				window.location.reload();
				return new Promise<{ default: T }>(() => {});
			}

			sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
			throw error;
		}),
	);
}

export function clearChunkReloadFlag(): void {
	sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
}
